/**
 * Studio Courses API Client
 *
 * Wraps backend contract `studio-series-courses-api-contract-v3.md` §6.
 * Identical surface to studio-series with `/studio/courses/` prefix and the
 * course-specific `show_on_catalog` / `purchase_link` extras. The atomic
 * video-move endpoint is video-scoped (`/studio/videos/{uuid}/move`) and
 * lives in `studio-series.client.ts`; this module re-exports it so course
 * consumers can `import { moveVideo } from '@/api/client/studio-courses.client'`.
 *
 * BE registers update routes as POST only — do NOT spoof `_method=PATCH`.
 */

import type {
  AttachVideoResponse,
  BatchAttachVideosPayload,
  BatchAttachVideosResponse,
  DetachVideoResponse,
  ReorderVideosPayload,
  ReorderVideosResponse,
} from '@/types/studio-series';
import type {
  CreateStudioCoursePayload,
  StudioCourse,
  StudioCourseDetail,
  StudioCourseDetailResponse,
  StudioCourseListFilters,
  StudioCourseListResponse,
  StudioCourseResponse,
  UpdateStudioCoursePayload,
} from '@/types/studio-courses';
import { apiClient } from './base-client';
import { studioSeriesClient } from './studio-series.client';

function buildCourseFormData(
  payload: CreateStudioCoursePayload | UpdateStudioCoursePayload
): FormData {
  const form = new FormData();
  if (payload.title !== undefined) form.append('title', payload.title);
  if (payload.slug !== undefined) form.append('slug', payload.slug);
  if (payload.description !== undefined) form.append('description', payload.description);
  if (payload.language !== undefined) form.append('language', payload.language);
  if (payload.thumbnail !== undefined) form.append('thumbnail', payload.thumbnail);
  if (payload.categories !== undefined) {
    payload.categories.forEach((id, i) => form.append(`categories[${i}]`, String(id)));
  }
  if (payload.show_on_catalog !== undefined) {
    form.append('show_on_catalog', payload.show_on_catalog ? '1' : '0');
  }
  if (payload.purchase_link !== undefined) form.append('purchase_link', payload.purchase_link);
  if (payload.is_free !== undefined) form.append('is_free', payload.is_free ? '1' : '0');
  // BE prohibits suggested_price while is_free=true — only send a concrete value.
  if (payload.suggested_price !== undefined && payload.suggested_price !== null) {
    form.append('suggested_price', String(payload.suggested_price));
  }
  return form;
}

function buildListParams(filters?: StudioCourseListFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (filters?.page !== undefined) params.page = filters.page;
  if (filters?.per_page !== undefined) params.per_page = Math.min(filters.per_page, 100);
  if (filters?.status !== undefined) params.status = filters.status;
  if (filters?.q !== undefined) params.q = filters.q;
  return params;
}

export const studioCoursesClient = {
  list: async (
    filters?: StudioCourseListFilters,
    serverToken?: string
  ): Promise<StudioCourseListResponse> => {
    const params = buildListParams(filters);
    return apiClient.get<StudioCourseListResponse>('/studio/courses', {
      ...(Object.keys(params).length > 0 ? { params } : {}),
      ...(serverToken !== undefined ? { serverToken } : {}),
    });
  },

  get: async (uuid: string, serverToken?: string): Promise<StudioCourseDetail> => {
    const data = await apiClient.get<StudioCourseDetailResponse>(
      `/studio/courses/${uuid}`,
      serverToken !== undefined ? { serverToken } : undefined
    );
    return data.course;
  },

  create: async (payload: CreateStudioCoursePayload): Promise<StudioCourse> => {
    const form = buildCourseFormData(payload);
    const data = await apiClient.post<StudioCourseResponse>('/studio/courses', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.course;
  },

  update: async (uuid: string, payload: UpdateStudioCoursePayload): Promise<StudioCourse> => {
    const form = buildCourseFormData(payload);
    const data = await apiClient.post<StudioCourseResponse>(`/studio/courses/${uuid}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.course;
  },

  clearThumbnail: async (uuid: string): Promise<StudioCourse> => {
    const data = await apiClient.delete<StudioCourseResponse>(`/studio/courses/${uuid}/thumbnail`);
    return data.course;
  },

  submitForReview: async (uuid: string): Promise<StudioCourse> => {
    const data = await apiClient.post<StudioCourseResponse>(
      `/studio/courses/${uuid}/submit-for-review`,
      {}
    );
    return data.course;
  },

  withdrawSubmission: async (uuid: string): Promise<StudioCourse> => {
    const data = await apiClient.post<StudioCourseResponse>(
      `/studio/courses/${uuid}/withdraw-submission`,
      {}
    );
    return data.course;
  },

  unpublish: async (uuid: string): Promise<StudioCourse> => {
    const data = await apiClient.patch<StudioCourseResponse>(
      `/studio/courses/${uuid}/unpublish`,
      {}
    );
    return data.course;
  },

  attachVideo: async (courseUuid: string, videoUuid: string): Promise<AttachVideoResponse> => {
    return apiClient.post<AttachVideoResponse>(
      `/studio/courses/${courseUuid}/videos/${videoUuid}`,
      {}
    );
  },

  detachVideo: async (courseUuid: string, videoUuid: string): Promise<DetachVideoResponse> => {
    return apiClient.delete<DetachVideoResponse>(
      `/studio/courses/${courseUuid}/videos/${videoUuid}`
    );
  },

  reorderVideos: async (
    courseUuid: string,
    payload: ReorderVideosPayload
  ): Promise<ReorderVideosResponse> => {
    return apiClient.patch<ReorderVideosResponse>(
      `/studio/courses/${courseUuid}/videos/reorder`,
      payload
    );
  },

  moveVideo: studioSeriesClient.moveVideo,

  /**
   * Cross-type endpoint — accepts both `target_type='course'` and
   * `target_type='series'`. Lives under courses for FE callsite locality even
   * though the BE route is video-scoped. v3.1 §3 contract.
   */
  batchAttachVideos: async (
    payload: BatchAttachVideosPayload
  ): Promise<BatchAttachVideosResponse> => {
    return apiClient.post<BatchAttachVideosResponse>('/studio/videos/batch-attach', payload);
  },
};
